// src/bytes.ts
var encoder = new TextEncoder();
var decoder = new TextDecoder("utf-8");
function encodeString(text) {
  return encoder.encode(text);
}
function decodeString(bytes) {
  return decoder.decode(bytes);
}
function byteLength(text) {
  return encoder.encode(text).length;
}
function concatBytes(...arrays) {
  const totalLength = arrays.reduce((sum, arr) => sum + arr.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const arr of arrays) {
    result.set(arr, offset);
    offset += arr.length;
  }
  return result;
}
function allocBytes(size) {
  return new Uint8Array(size);
}
function fromBytes(values) {
  return new Uint8Array(values);
}
function bytesEqual(a, b) {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}
function startsWith(bytes, prefix) {
  if (bytes.length < prefix.length) {
    return false;
  }
  for (let i = 0; i < prefix.length; i++) {
    if (bytes[i] !== prefix[i]) {
      return false;
    }
  }
  return true;
}
function startsWithString(bytes, prefix) {
  return startsWith(bytes, encodeString(prefix));
}
function indexOfString(bytes, needle, fromIndex = 0) {
  const needleBytes = encodeString(needle);
  const endIndex = bytes.length - needleBytes.length;
  outer: for (let i = fromIndex; i <= endIndex; i++) {
    for (let j = 0; j < needleBytes.length; j++) {
      if (bytes[i + j] !== needleBytes[j]) {
        continue outer;
      }
    }
    return i;
  }
  return -1;
}
function decodeFirstRune(bytes, offset = 0) {
  if (offset >= bytes.length) {
    return [null, 0];
  }
  const firstByte = bytes[offset];
  if (firstByte === void 0) {
    return [null, 0];
  }
  let byteLen;
  if ((firstByte & 128) === 0) {
    byteLen = 1;
  } else if ((firstByte & 224) === 192) {
    byteLen = 2;
  } else if ((firstByte & 240) === 224) {
    byteLen = 3;
  } else if ((firstByte & 248) === 240) {
    byteLen = 4;
  } else {
    byteLen = 1;
  }
  if (offset + byteLen > bytes.length) {
    return [null, 0];
  }
  const slice = bytes.subarray(offset, offset + byteLen);
  const decoded = decoder.decode(slice);
  if (decoded.length === 0) {
    return [null, 0];
  }
  const codePoint = decoded.codePointAt(0);
  if (codePoint === void 0) {
    return [null, 0];
  }
  const firstChar = String.fromCodePoint(codePoint);
  return [firstChar, byteLen];
}
function sliceBytes(bytes, start, end) {
  return bytes.subarray(start, end);
}
function copyBytes(bytes) {
  return new Uint8Array(bytes);
}

// src/sequences.ts
var ESC = "\x1B";
var CSI = `${ESC}[`;
var OSC = `${ESC}]`;
var ST = `${ESC}\\`;
var BEL = "\x07";
var CURSOR_SHOW = `${CSI}?25h`;
var CURSOR_HIDE = `${CSI}?25l`;
var CURSOR_HOME = `${CSI}H`;
function cursorTo(row, col) {
  return `${CSI}${row};${col}H`;
}
function cursorUp(n = 1) {
  return `${CSI}${n}A`;
}
function cursorDown(n = 1) {
  return `${CSI}${n}B`;
}
function cursorForward(n = 1) {
  return `${CSI}${n}C`;
}
function cursorBackward(n = 1) {
  return `${CSI}${n}D`;
}
var CURSOR_SAVE = `${CSI}s`;
var CURSOR_RESTORE = `${CSI}u`;
var CLEAR_SCREEN = `${CSI}2J`;
var CLEAR_SCREEN_DOWN = `${CSI}0J`;
var CLEAR_SCREEN_UP = `${CSI}1J`;
var CLEAR_LINE = `${CSI}2K`;
var CLEAR_LINE_END = `${CSI}0K`;
var CLEAR_LINE_START = `${CSI}1K`;
var ALT_SCREEN_ON = `${CSI}?1049h`;
var ALT_SCREEN_OFF = `${CSI}?1049l`;
var MOUSE_CELL_ON = `${CSI}?1002h`;
var MOUSE_ALL_ON = `${CSI}?1003h`;
var MOUSE_SGR_ON = `${CSI}?1006h`;
var MOUSE_CELL_OFF = `${CSI}?1002l`;
var MOUSE_ALL_OFF = `${CSI}?1003l`;
var MOUSE_SGR_OFF = `${CSI}?1006l`;
var BRACKETED_PASTE_ON = `${CSI}?2004h`;
var BRACKETED_PASTE_OFF = `${CSI}?2004l`;
var BRACKETED_PASTE_START = `${CSI}200~`;
var BRACKETED_PASTE_END = `${CSI}201~`;
var REPORT_FOCUS_ON = `${CSI}?1004h`;
var REPORT_FOCUS_OFF = `${CSI}?1004l`;
var FOCUS_IN = `${CSI}I`;
var FOCUS_OUT = `${CSI}O`;
function setWindowTitle(title) {
  return `${OSC}0;${title}${BEL}`;
}
var RESET = `${CSI}0m`;
var BOLD = `${CSI}1m`;
var DIM = `${CSI}2m`;
var ITALIC = `${CSI}3m`;
var UNDERLINE = `${CSI}4m`;
var BLINK = `${CSI}5m`;
var REVERSE = `${CSI}7m`;
var HIDDEN = `${CSI}8m`;
var STRIKETHROUGH = `${CSI}9m`;
function fg256(colorIndex) {
  return `${CSI}38;5;${colorIndex}m`;
}
function bg256(colorIndex) {
  return `${CSI}48;5;${colorIndex}m`;
}
function fgRGB(r, g, b) {
  return `${CSI}38;2;${r};${g};${b}m`;
}
function bgRGB(r, g, b) {
  return `${CSI}48;2;${r};${g};${b}m`;
}
function scrollUp(n = 1) {
  return `${CSI}${n}S`;
}
function scrollDown(n = 1) {
  return `${CSI}${n}T`;
}
function setScrollRegion(top, bottom) {
  return `${CSI}${top};${bottom}r`;
}
var RESET_SCROLL_REGION = `${CSI}r`;

// src/style/index.ts
var ESC2 = "\x1B[";
var CODES = {
  // Modifiers (open, close)
  bold: ["1", "22"],
  dim: ["2", "22"],
  italic: ["3", "23"],
  underline: ["4", "24"],
  inverse: ["7", "27"],
  hidden: ["8", "28"],
  strikethrough: ["9", "29"],
  // Basic foreground colors (30-37)
  black: ["30", "39"],
  red: ["31", "39"],
  green: ["32", "39"],
  yellow: ["33", "39"],
  blue: ["34", "39"],
  magenta: ["35", "39"],
  cyan: ["36", "39"],
  white: ["37", "39"],
  // Bright foreground colors (90-97)
  blackBright: ["90", "39"],
  redBright: ["91", "39"],
  greenBright: ["92", "39"],
  yellowBright: ["93", "39"],
  blueBright: ["94", "39"],
  magentaBright: ["95", "39"],
  cyanBright: ["96", "39"],
  whiteBright: ["97", "39"],
  // Basic background colors (40-47)
  bgBlack: ["40", "49"],
  bgRed: ["41", "49"],
  bgGreen: ["42", "49"],
  bgYellow: ["43", "49"],
  bgBlue: ["44", "49"],
  bgMagenta: ["45", "49"],
  bgCyan: ["46", "49"],
  bgWhite: ["47", "49"],
  // Bright background colors (100-107)
  bgBlackBright: ["100", "49"],
  bgRedBright: ["101", "49"],
  bgGreenBright: ["102", "49"],
  bgYellowBright: ["103", "49"],
  bgBlueBright: ["104", "49"],
  bgMagentaBright: ["105", "49"],
  bgCyanBright: ["106", "49"],
  bgWhiteBright: ["107", "49"]
};
function applyStyle(text, open, close) {
  const openSeq = `${ESC2}${open}m`;
  const closeSeq = `${ESC2}${close}m`;
  const replaced = text.replace(
    new RegExp(closeSeq.replace(/[[\](){}|^$+*?.\\]/g, "\\$&"), "g"),
    closeSeq + openSeq
  );
  return openSeq + replaced + closeSeq;
}
function hexToRgb(hex) {
  const clean = hex.replace(/^#/, "");
  const num = parseInt(clean, 16);
  if (clean.length === 3) {
    const r2 = (num >> 8 & 15) * 17;
    const g2 = (num >> 4 & 15) * 17;
    const b2 = (num & 15) * 17;
    return [r2, g2, b2];
  }
  const r = num >> 16 & 255;
  const g = num >> 8 & 255;
  const b = num & 255;
  return [r, g, b];
}
function createStyle(colorSupport) {
  const enabled = colorSupport.level > 0;
  function build(stack) {
    const fn = (text) => {
      if (!enabled || stack.length === 0) {
        return text;
      }
      let result = text;
      for (let i = stack.length - 1; i >= 0; i--) {
        const [open, close] = stack[i];
        result = applyStyle(result, open, close);
      }
      return result;
    };
    const codeNames = Object.keys(CODES);
    for (const name of codeNames) {
      const [open, close] = CODES[name];
      Object.defineProperty(fn, name, {
        get() {
          return build([...stack, [open, close]]);
        }
      });
    }
    Object.defineProperty(fn, "gray", {
      get() {
        const [open, close] = CODES.blackBright;
        return build([...stack, [open, close]]);
      }
    });
    Object.defineProperty(fn, "grey", {
      get() {
        const [open, close] = CODES.blackBright;
        return build([...stack, [open, close]]);
      }
    });
    Object.defineProperty(fn, "hex", {
      value: (color) => {
        if (!enabled || !colorSupport.has16m) {
          return build(stack);
        }
        const [r, g, b] = hexToRgb(color);
        return build([...stack, [`38;2;${r};${g};${b}`, "39"]]);
      },
      writable: true
    });
    Object.defineProperty(fn, "rgb", {
      value: (r, g, b) => {
        if (!enabled || !colorSupport.has16m) {
          return build(stack);
        }
        return build([...stack, [`38;2;${r};${g};${b}`, "39"]]);
      },
      writable: true
    });
    Object.defineProperty(fn, "bgHex", {
      value: (color) => {
        if (!enabled || !colorSupport.has16m) {
          return build(stack);
        }
        const [r, g, b] = hexToRgb(color);
        return build([...stack, [`48;2;${r};${g};${b}`, "49"]]);
      },
      writable: true
    });
    Object.defineProperty(fn, "bgRgb", {
      value: (r, g, b) => {
        if (!enabled || !colorSupport.has16m) {
          return build(stack);
        }
        return build([...stack, [`48;2;${r};${g};${b}`, "49"]]);
      },
      writable: true
    });
    Object.defineProperty(fn, "ansi256", {
      value: (code) => {
        if (!enabled || !colorSupport.has256) {
          return build(stack);
        }
        return build([...stack, [`38;5;${code}`, "39"]]);
      },
      writable: true
    });
    Object.defineProperty(fn, "bgAnsi256", {
      value: (code) => {
        if (!enabled || !colorSupport.has256) {
          return build(stack);
        }
        return build([...stack, [`48;5;${code}`, "49"]]);
      },
      writable: true
    });
    return fn;
  }
  return build([]);
}
function createAlwaysEnabledStyle() {
  return createStyle({ level: 3, has256: true, has16m: true });
}

// src/detect.ts
function isNodeEnvironment() {
  const g = globalThis;
  const proc = g["process"];
  return !!proc && typeof proc["versions"]?.["node"] === "string";
}
function isBrowserEnvironment() {
  const g = globalThis;
  return typeof g["window"] !== "undefined" && typeof g["document"] !== "undefined" && !isNodeEnvironment();
}

export { ALT_SCREEN_OFF, ALT_SCREEN_ON, BEL, BLINK, BOLD, BRACKETED_PASTE_END, BRACKETED_PASTE_OFF, BRACKETED_PASTE_ON, BRACKETED_PASTE_START, CLEAR_LINE, CLEAR_LINE_END, CLEAR_LINE_START, CLEAR_SCREEN, CLEAR_SCREEN_DOWN, CLEAR_SCREEN_UP, CSI, CURSOR_HIDE, CURSOR_HOME, CURSOR_RESTORE, CURSOR_SAVE, CURSOR_SHOW, DIM, ESC, FOCUS_IN, FOCUS_OUT, HIDDEN, ITALIC, MOUSE_ALL_OFF, MOUSE_ALL_ON, MOUSE_CELL_OFF, MOUSE_CELL_ON, MOUSE_SGR_OFF, MOUSE_SGR_ON, OSC, REPORT_FOCUS_OFF, REPORT_FOCUS_ON, RESET, RESET_SCROLL_REGION, REVERSE, ST, STRIKETHROUGH, UNDERLINE, allocBytes, bg256, bgRGB, byteLength, bytesEqual, concatBytes, copyBytes, createAlwaysEnabledStyle, createStyle, cursorBackward, cursorDown, cursorForward, cursorTo, cursorUp, decodeFirstRune, decodeString, encodeString, fg256, fgRGB, fromBytes, indexOfString, isBrowserEnvironment, isNodeEnvironment, scrollDown, scrollUp, setScrollRegion, setWindowTitle, sliceBytes, startsWith, startsWithString };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map